import React, { forwardRef, type MouseEvent, type ReactNode } from 'react';

import Button from '@atlaskit/button/default/button';
import type { IconProp } from '@atlaskit/button/variants/types';
import { cssMap } from '@atlaskit/css';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { PaginatedPickerIntlProvider } from '../../../common/utils';

import { ClearButton } from './clear-button';
import { LoadingStateAnimationWrapper } from './loading-state';
import { ButtonWrapper, StyledClose } from './styled';

export type PickerTriggerProps = {
	ariaLabel?: string;
	children: ReactNode;
	hasValues: boolean;
	hideSelectedIndicator?: boolean;
	isDisabled?: boolean;
	isInvalid: boolean;
	isLoading?: boolean;
	isSelected: boolean;
	onClear?: () => void;
	onClick: () => void;
	testId?: string;
	triggerIcon?: IconProp;
};

const textPadding = cssMap({
	root: {
		paddingRight: token('space.150'),
	},
});

export const PickerTrigger: React.ForwardRefExoticComponent<
	PickerTriggerProps & React.RefAttributes<HTMLButtonElement>
> = forwardRef<HTMLButtonElement, PickerTriggerProps>(
	(
		{
			children,
			isInvalid,
			isSelected,
			hideSelectedIndicator,
			triggerIcon,
			isLoading,
			hasValues,
			onClick,
			onClear,
			testId,
			isDisabled,
			ariaLabel,
		}: PickerTriggerProps,
		ref,
	) => {
		if (isLoading) {
			return (
				<PaginatedPickerIntlProvider>
					<LoadingStateAnimationWrapper>
						<Button
							isSelected={!hideSelectedIndicator && isSelected}
							iconBefore={triggerIcon}
							testId={testId}
						>
							{children}:
						</Button>
					</LoadingStateAnimationWrapper>
				</PaginatedPickerIntlProvider>
			);
		}

		const afterTriggerIcon = onClear ? (
			<StyledClose>
				<ClearButton
					onClick={(e: MouseEvent) => {
						e.stopPropagation();
						onClear?.();
					}}
					label="Remove criteria"
				/>
			</StyledClose>
		) : (
			<ChevronDownIcon color="currentColor" label="" size="small" />
		);

		return (
			<PaginatedPickerIntlProvider>
				<ButtonWrapper>
					<Button
						appearance={isInvalid ? 'danger' : 'default'}
						isSelected={!hideSelectedIndicator && (isSelected || hasValues)}
						iconBefore={triggerIcon}
						iconAfter={() => afterTriggerIcon}
						onClick={onClick}
						ref={ref}
						testId={testId}
						aria-expanded={isSelected}
						isDisabled={isDisabled}
						aria-label={ariaLabel && ariaLabel}
					>
						{onClear ? <Box xcss={textPadding.root}>{children}</Box> : children}
					</Button>
				</ButtonWrapper>
			</PaginatedPickerIntlProvider>
		);
	},
);

export default PickerTrigger;
